import { motion } from 'framer-motion';
import { Layers, UserCheck, Link2 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  VCard Preview (extracted from DifferentiatorSection)                */
/* ------------------------------------------------------------------ */

function VCardPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="rounded-2xl border bg-card p-5 md:p-6 shadow-subtle"
    >
      {/* Finding header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
            Finding
          </p>
          <p className="text-sm md:text-base font-semibold" role="heading" aria-level={3}>
            Participants adapt coping strategies based on institutional trust
          </p>
        </div>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        <span className="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] md:text-[11px] font-medium text-primary border border-primary/20">
          RQ1
        </span>
        <span className="px-2 py-0.5 rounded-full bg-evidence/8 text-[10px] md:text-[11px] font-medium text-evidence border border-evidence/20">
          3 quotes
        </span>
        <span className="px-2 py-0.5 rounded-full bg-evidence/8 text-[10px] md:text-[11px] font-medium text-evidence border border-evidence/20">
          2 sources
        </span>
        <span className="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] md:text-[11px] font-medium text-primary border border-primary/20">
          Confidence: High
        </span>
      </div>

      {/* Quote excerpts with evidence tether */}
      <div className="space-y-3 relative">
        <svg
          className="absolute -left-1 top-0 h-full w-3 pointer-events-none"
          aria-hidden="true"
        >
          <line
            x1="6" y1="4" x2="6" y2="100%"
            stroke="hsl(var(--evidence) / 0.25)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
        </svg>

        <div className="rounded-lg border bg-paper p-3 ml-3">
          <p className="text-xs text-muted-foreground leading-relaxed italic">
            "When I felt the system was fair, I was more willing to be open about my difficulties..."
          </p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/70">
              Source 1 · Lines 42–44
            </span>
            <span
              className="text-[10px] font-medium text-evidence"
              aria-hidden="true"
            >
              Open context
            </span>
          </div>
        </div>
        <div className="rounded-lg border bg-paper p-3 ml-3">
          <p className="text-xs text-muted-foreground leading-relaxed italic">
            "Trust in the process made me feel like my voice actually mattered, so I shared more."
          </p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/70">
              Source 2 · Lines 78–80
            </span>
            <span
              className="text-[10px] font-medium text-evidence"
              aria-hidden="true"
            >
              Open context
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Pillars                                                            */
/* ------------------------------------------------------------------ */

const pillars = [
  {
    icon: Layers,
    title: 'Grounded in your material',
    body: 'Codes and themes emerge from what you uploaded, not from what the model thinks research usually looks like. Your data leads. The analysis follows.',
  },
  {
    icon: UserCheck,
    title: 'You stay in control',
    body: 'Verithos accelerates the workflow. You shape the analysis, decide what stays, and own the conclusions. The AI suggests. You determine.',
  },
  {
    icon: Link2,
    title: 'Traceable end to end',
    body: 'Quote-level evidence throughout. Every insight has a source. Every source is one click away. The chain never breaks.',
  },
];

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

export function WhyVerithosSection() {
  return (
    <section
      id="why-verithos"
      className="py-12 sm:py-16 md:py-20 2xl:py-24 relative scroll-mt-24"
    >
      <div className="max-w-6xl 2xl:max-w-7xl mx-auto px-5 sm:px-6 2xl:px-8">
        {/* Two-column: text + VCard */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 2xl:gap-20 items-start">
          {/* Left: narrative */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-evidence/10 border border-evidence/20 mb-5">
              <span className="text-[10px] md:text-xs font-semibold text-evidence uppercase tracking-wider">
                Why It Matters
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-3xl 2xl:text-4xl font-semibold tracking-tight text-balance mb-6 md:mb-8 leading-tight">
              Most tools give you outputs.{' '}
              <span className="text-primary">Verithos gives you a position you can defend.</span>
            </h2>

            <div className="space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed max-w-lg">
              <p>
                Every serious research project reaches the same inflection point.
                You have the material. Now you need to know what it means,
                and be prepared to justify every conclusion you draw from it.
              </p>
              <p>
                Generic AI gets you to an answer. Verithos gets you to an answer
                with a chain of evidence behind it. Every finding links to a code.
                Every code links to a quote. Every quote links to its source.
              </p>
              <p>
                That chain is what separates insight from assertion.
                It's what makes findings hold up when they need to.
              </p>
            </div>
          </motion.div>

          {/* Right: VCard preview */}
          <div className="md:sticky md:top-28">
            <VCardPreview />
          </div>
        </div>

        {/* Three pillars */}
        <div className="mt-14 md:mt-20 grid sm:grid-cols-3 gap-6 md:gap-8">
          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * i, duration: 0.5 }}
              className="relative rounded-xl border border-border/60 bg-card p-6 md:p-7"
            >
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <pillar.icon className="h-5 w-5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-2">
                {pillar.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {pillar.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-xs text-muted-foreground/60 italic text-center"
        >
          Verithos suggests patterns. You decide what they mean.
        </motion.p>
      </div>
    </section>
  );
}
